// Power-up configurations
export interface PowerUpConfig {
  type: string;
  name: string;
  duration: number; // milliseconds, 0 = instant
  isActive: boolean; // active vs passive
  color: string;
  description: string;
  weight: number; // probability weight for drops
}

// Power-up type enum matching original game
export enum PowerUpType {
  SHIELD = 1,
  RAPID = 2,
  LIFE = 3,
  SCORE = 4,
  BASS = 5,
  TEMPO = 6,
  DOUBLE = 7,
  TRIPLE = 8,
  NUKE = 9,
  FREEZE = 10,
  MAGNET = 11
}

export const POWERUP_TYPES: Record<string, PowerUpConfig> = {
  // Active Power-ups (temporary abilities)
  NEON_SHIELD: {
    type: 'neon_shield',
    name: 'Neon Shield',
    duration: 8000,
    isActive: true,
    color: '#00ffff',
    description: 'Protects from 3 hits',
    weight: 20
  },
  RAPID_SHOT: {
    type: 'rapid_shot',
    name: 'Rapid Shot',
    duration: 10000,
    isActive: true,
    color: '#ff6600',
    description: 'Increases fire rate by 50%',
    weight: 18
  },
  EXTRA_LIFE: {
    type: 'extra_life',
    name: 'Extra Life',
    duration: 0,
    isActive: false,
    color: '#ff0000',
    description: 'Gain +1 life',
    weight: 8
  },
  SCORE_MULTIPLIER: {
    type: 'score_mult',
    name: 'Score Multiplier',
    duration: 12000,
    isActive: true,
    color: '#ffff00',
    description: 'All scores worth 2x',
    weight: 12
  },
  BASS_BOOST: {
    type: 'bass_boost',
    name: 'Bass Boost',
    duration: 15000,
    isActive: true,
    color: '#ff00ff',
    description: 'Increase weapon damage',
    weight: 14
  },
  TEMPO_BOOST: {
    type: 'tempo_boost',
    name: 'Tempo Boost',
    duration: 12000,
    isActive: true,
    color: '#00ff00',
    description: 'Increase movement speed',
    weight: 16
  },
  DOUBLE_CHAIN: {
    type: 'double_chain',
    name: 'Double Chain Mode',
    duration: 10000,
    isActive: true,
    color: '#ff00ff',
    description: 'Earn double citizens per wave',
    weight: 15
  },
  TRIPLE_SHOT: {
    type: 'triple_shot',
    name: 'Triple Shot',
    duration: 10000,
    isActive: true,
    color: '#ffaa00',
    description: 'Shoot 3 bullets in a spread',
    weight: 12
  },
  STARBURST_BOMB: {
    type: 'starburst',
    name: 'Starburst Bomb',
    duration: 0,
    isActive: true,
    color: '#ffffff',
    description: 'Clears all enemies on screen',
    weight: 6
  },
  TIME_FREEZE: {
    type: 'time_freeze',
    name: 'Time Freeze',
    duration: 8000,
    isActive: true,
    color: '#4444ff',
    description: 'Slows all enemies to 20% speed',
    weight: 10
  },
  AUTO_COLLECT: {
    type: 'auto_collect',
    name: 'Auto-Collect',
    duration: 15000,
    isActive: true,
    color: '#00ff88',
    description: 'Automatically collect nearby power-ups',
    weight: 13
  },
  // Chain-specific power-ups
  REPAIR: {
    type: 'repair',
    name: 'Chain Repair',
    duration: 0,
    isActive: true,
    color: '#00ff00',
    description: 'Instantly restores damaged chain segments',
    weight: 20
  },
  SHIELD: {
    type: 'shield',
    name: 'Chain Shield',
    duration: 8000,
    isActive: true,
    color: '#4444ff',
    description: 'Temporary invincibility for the chain',
    weight: 15
  },
  // Weapon Power-Ups
  WAVE_SIGNAL_UP: {
    type: 'wave_signal',
    name: 'Wave Signal',
    duration: 12000,
    isActive: true,
    color: '#00ffff',
    description: 'Slow piercing waves',
    weight: 10
  },
  DUAL_CANNON_UP: {
    type: 'dual_cannon',
    name: 'Dual Cannon',
    duration: 12000,
    isActive: true,
    color: '#ff4444',
    description: 'Two parallel shots',
    weight: 10
  },
  MACHINE_GUN_UP: {
    type: 'machine_gun',
    name: 'Machine Gun',
    duration: 12000,
    isActive: true,
    color: '#ffff00',
    description: 'Ultra-fast fire rate',
    weight: 10
  },
  SHOTGUN_UP: {
    type: 'shotgun_blast',
    name: 'Shotgun Blast',
    duration: 12000,
    isActive: true,
    color: '#ff8800',
    description: 'Wide spread of 5 shots',
    weight: 10
  },
  SUPER_BLAST_UP: {
    type: 'fm_super_blast',
    name: 'FM Super Blast',
    duration: 12000,
    isActive: true,
    color: '#ff00ff',
    description: 'Massive powerful shots',
    weight: 8
  },
};

export function getRandomPowerUp(): PowerUpConfig {
  const types = Object.values(POWERUP_TYPES);
  const totalWeight = types.reduce((sum, type) => sum + type.weight, 0);
  let random = Math.random() * totalWeight;
  
  for (const type of types) {
    random -= type.weight;
    if (random <= 0) {
      return type;
    }
  }
  
  return types[0];
}
